import cuid from 'cuid'
import captureError from '@disco/utils/captureError'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import uniqBy from 'lodash/uniqBy'
import NavigationService from '@disco/state-management/NavigationService'
import type { ArtistId } from 'types/artist'
import type { Group, GroupId } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { GetState, Thunk } from 'types/redux'
import {
  copyPlaylistTracksToDisco,
  updateRecentsWithNewItem,
  copyBulkTracksToDisco,
} from '@disco/state-management/actions/ExplorerActions'

import type {
  Shortlist,
  ShortlistSection,
  ShortlistTrackItem,
} from 'types/shortlist'
import type { Track } from 'types/track'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  loadAllArtistTracks,
  updateArtist,
  updateArtistImage,
} from '@disco/state-management/actions/artist-actions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import {
  addAggregatedTrackToDisco,
  adjustPlaylistPositionInExplorerPane,
  alterPlaylistGroups,
  alterTrackGroups,
  switchPaneView,
} from '@disco/state-management/actions/ExplorerActions'
import {
  playTrack,
  resetPlaybackState,
} from '@disco/state-management/actions/MediaActions'
import {
  fetchNestedTracks,
  loadBatch,
  mapResponseToReducerObjects,
} from '@disco/state-management/actions/PlaylistTrackActions'
import { initEditedTrack } from '@disco/state-management/actions/ServerActions'
import { deleteShortlist } from '@disco/state-management/actions/shortlist-upload-actions'
import api from '@disco/api/server'
import {
  LOAD_SAVED_STATE,
  LOAD_SAVED_STATE_ERROR,
  SHORTLIST_ACTIVATE,
  SHORTLIST_DEACTIVATE,
  SHORTLIST_NEW,
  SHORTLIST_NEW_START_CREATE,
  SHORTLIST_PLAYLIST_TRACKS_ADD,
  SHORTLIST_RESET,
  SHORTLIST_SECTION_ADD,
  SHORTLIST_SECTION_REMOVE,
  SHORTLIST_SECTION_UPDATE,
  SHORTLIST_SET_LOADING,
  SHORTLIST_TEMP_ITEM_INSERT,
  SHORTLIST_TEMP_ITEM_PROGRESS_UPDATE,
  SHORTLIST_TEMP_ITEM_REMOVE,
  SHORTLIST_TEMP_ITEM_UPDATE,
  SHORTLIST_UPDATE,
  START_SAVING_STATE,
  USER_SETTINGS_UPDATE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { SHOWCASES, TRACK_NESTING } from '@disco/constants/FeatureTypes'
import {
  CUSTOM_CHANNEL,
  INBOX_TYPES,
  LIBRARY_TYPES,
  PERSONAL_LIBRARY,
  TEAM_LIBRARY,
  TERRITORY,
} from '@disco/constants/GroupTypes'
import { PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import {
  NESTED_TRACK_ALREADY_IN_PLAYLIST,
  VERSION_OF_MAIN_TRACK_ALREADY_IN_PLAYLIST,
} from '@disco/constants/Nesting'
import {
  type SORT_DIRECTION,
  SORT_DIRECTION_DESC,
  TRACK_DATE_SORT,
} from '@disco/constants/SortTypes'
import { TRACK_VIEW } from '@disco/constants/ViewTypes'
import {
  getArtist,
  getArtistTracks,
} from '@disco/state-management/selectors/artists'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getExplorerFirstPane } from '@disco/state-management/selectors/explorer'
import { getGroups } from '@disco/state-management/selectors/groups'
import { getPlaylists } from '@disco/state-management/selectors/playlists'
import {
  getDefaultPlaylistOptions,
  getEmptyShortlists,
  getShortlistById,
  getShortlistTempItem,
  isNestingEnabledInShortlist,
} from '@disco/state-management/selectors/shortlists'
import { getTrack, getTracks } from '@disco/state-management/selectors/tracks'
import { parseServerError } from '@disco/utils/captureError'
import { getInitialMetadataValues } from '@disco/utils/commonMetadata'
import { base64ToBlob } from '@disco/utils/convertImage'
import {
  getCommonGroups,
  getDefaultGroups,
  getPersonalLibrary,
  getTeamLibrary,
  itemRequireAddingToTeamLib,
} from '@disco/utils/groups'
import { moveItems2 } from '@disco/utils/immutable'
import { pushUINotification } from '@disco/utils/notifications'
import { removeBinaryImagesInShortlist } from '@disco/utils/shortlist'
import type { SortDirection, SortKey } from '@disco/utils/track-sorting'
import { sortItems, sortItemsInSections } from '@disco/utils/track-sorting'
import { getNestedTracksCount } from '@disco/utils/tracks'

function _filterPlaylistGroups(groups, playlist) {
  const result = groups.filter(group => playlist.groups.includes(group.url))
  // if there are other groups besides inboxes, return groups as is
  if (result.filter(g => INBOX_TYPES.indexOf(g.group_type) === -1).length)
    return result
  // if there are only inboxes in groups, add libraries
  return [...getDefaultShortlistGroups(groups), ...result]
}

/**
 * Adjusts section positions after tracks are removed from a shortlist.
 * @param shortlist - The shortlist containing sections to adjust
 * @param trackIndexes - Array of track indexes that were removed
 * @returns Updated array of shortlist sections with adjusted positions
 */
export function adjustSectionsOnRemovedTrackIndexes(
  shortlist: Shortlist,
  trackIndexes: number[],
): Array<ShortlistSection> {
  if (shortlist.sections.length === 0) {
    return shortlist.sections
  }

  let adjustedSections: ShortlistSection[] = shortlist.sections.slice()
  const sortedTrackIndexes = trackIndexes.sort((a, b) => b - a)
  sortedTrackIndexes.forEach(index => {
    adjustedSections = adjustedSections.map(s => {
      if (s.position > index) return { ...s, position: s.position - 1 }
      return s
    })
  })
  return adjustedSections
}
/**
 * Adjusts section positions after tracks are moved within a shortlist.
 * @param shortlist - The shortlist containing sections to adjust
 * @param trackIndexes - Array of track indexes that were moved
 * @param position - New position where tracks were moved
 * @param targetSection - Target section where tracks were moved
 * @returns Updated array of shortlist sections with adjusted positions
 */
export function adjustSectionsOnMovedTrackIndexes(
  shortlist: Shortlist,
  trackIndexes: number[],
  position: number,
  targetSection: ShortlistSection,
): ShortlistSection[] {
  const targetSectionIndex = targetSection
    ? shortlist.sections.findIndex(s => s.id === targetSection.id)
    : -1
  const adjustmentOffsets = shortlist.sections.map(s => {
    return trackIndexes.filter(index => index < s.position).length
  })
  return shortlist.sections.map((s, sIndex) => {
    const adjustmentOffset = adjustmentOffsets[sIndex]

    if (adjustmentOffset === undefined) {
      return s
    }

    const normalizedPosition = s.position - adjustmentOffset
    const skipCorrection = targetSection
      ? sIndex <= targetSectionIndex
      : normalizedPosition < (position || 0)
    const correctedLength = skipCorrection ? 0 : trackIndexes.length
    return { ...s, position: normalizedPosition + correctedLength }
  })
}
/**
 * Gets the default groups for a new shortlist.
 * @param groups - Array of available groups
 * @returns Array of default groups for shortlists
 */
export const getDefaultShortlistGroups = (groups: Group[]): Group[] => {
  return uniqBy(
    [
      ...getDefaultGroups(groups),
      getTeamLibrary(groups),
      getPersonalLibrary(groups),
    ].filter(group => group !== null && group !== undefined),
    'id',
  )
}

export const activateShortlist =
  (shortlistId: string): ((dispatch: any) => void) =>
  (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: SHORTLIST_ACTIVATE,
      shortlistId,
    })
    setTimeout(
      () =>
        dispatch({
          type: SHORTLIST_DEACTIVATE,
          shortlistId,
        }),
      500,
    )
  }
/**
 * Creates a new shortlist with optional source playlist and configuration.
 * @param playlist - Optional source playlist to create shortlist from
 * @param title - Title for the new shortlist
 * @param createOptions - Configuration options for shortlist creation
 * @param createOptions.tracks - Array of tracks to add to new shortlist
 * @param createOptions.playlist - Playlist to add tracks from without additional properties
 * @param createOptions.duplicate - Whether to create a duplicate of existing playlist
 * @param createOptions.defaultFields - Default field values for the shortlist
 * @returns Thunk that creates a new shortlist
 */
export function createNewShortlist(
  playlist?: Playlist,
  title?: string,
  createOptions?: {
    tracks?: Track[]
    // add tracks to new shortlist
    playlist?: Playlist
    // add tracks from existing playlist to new shortlist without any additional playlist properties
    duplicate?: boolean
    // create new shortlist based on playlist from the first param, except erase reference to original and adjust title
    defaultFields?: Record<string, any>
  },
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const {
      user,
      groups,
      shortlists: { loaded, shortlistCreating, byId },
    } = getState()
    // defensive check
    if (
      !user.loggedIn ||
      !loaded ||
      (playlist?.playlist_id && shortlistCreating[playlist?.playlist_id])
    )
      return

    if (!createOptions?.duplicate) {
      // do nothing if playlist was already opened for editing
      const id = playlist
        ? Object.keys(byId).find(
            key => byId[key].playlist_id === playlist.playlist_id,
          )
        : null

      if (id) {
        const correspondingShortlist = byId[id]

        if (playlist && correspondingShortlist) {
          dispatch(activateShortlist(correspondingShortlist.id))
          return
        }
      }

      // Only for editing playlists
      dispatch({
        type: SHORTLIST_NEW_START_CREATE,
        result: {
          id: playlist?.playlist_id,
        },
      })
    }

    const combinedGroups = [
      ...user.groups,
      ...groups.groups.filter(
        group =>
          group.group_type === 'user' ||
          INBOX_TYPES.indexOf(group.group_type) > -1,
      ),
    ]
    const shortListGroups =
      playlist && playlist.groups
        ? uniqBy<Group>(_filterPlaylistGroups(combinedGroups, playlist), 'id')
        : getDefaultShortlistGroups(user.groups)
    const defaultPlaylistOptions = getDefaultPlaylistOptions(getState())
    const newShortlist: {
      type: string
      result: Partial<
        Omit<Shortlist, 'show_business_logo' | 'sort_direction'>
      > & {
        id: string
        show_business_logo?: boolean | null
        sort_direction?: SORT_DIRECTION | null
      }
    } = {
      type: SHORTLIST_NEW,
      result: {
        id: cuid(),
        title: title || '',
        default_zip_mode: get(
          user,
          'business.public_metadata.default_zip_mode',
          'optimized',
        ),
        share_mode: get(
          user,
          'business.public_metadata.share_mode',
          'standard',
        ),
        sort_key: playlist && playlist.sort_key ? playlist.sort_key : 'custom',
        sort_direction:
          playlist && playlist.sort_direction ? playlist.sort_direction : null,
        groups: shortListGroups,
        tags: [],
        ...defaultPlaylistOptions,
        ...createOptions?.defaultFields,
      },
    }

    if (playlist !== undefined) {
      api
        .getFullPlaylist(playlist)
        .then(result => {
          const fullPlaylist = mapResponseToReducerObjects(
            dispatch,
            getState,
            result,
          )
          dispatch(fetchNestedTracks(fullPlaylist.tracks))
          const items = getShortlistItems(fullPlaylist, getState)
          const {
            album_image_url,
            color,
            album_image,
            focal_points,
            name,
            types,
            playlist_id,
            is_client_version,
            download_links,
            show_lyrics,
            show_business_logo,
            show_contacts,
            showcase,
            include_nested_track_versions,
            share_stems,
            theme,
            sections,
            tags,
            general_message,
            general_message_html,
            default_zip_mode,
            share_mode,
            track_aliases,
            playlist_aliases,
            user_aliases,
            watermark,
            watermarking_status,
            playlist_type,
            release_date,
            tracks_album_image,
            tracks_artworks,
          } = fullPlaylist
          newShortlist.result = {
            ...newShortlist.result,
            album_image_url,
            color,
            album_image,
            focal_points,
            title: createOptions?.duplicate ? `${name} (copy)` : title ?? name,
            items,
            // @ts-expect-error - TODO: fix this
            types,
            playlist_id: createOptions?.duplicate ? null : playlist_id,
            is_client_version,
            download_links,
            show_lyrics,
            show_business_logo,
            show_contacts,
            showcase,
            // set default prop
            include_nested_track_versions:
              include_nested_track_versions === null
                ? false
                : include_nested_track_versions,
            // set default prop
            share_stems: share_stems === null ? false : share_stems,
            theme,
            general_message,
            general_message_html,
            sections: sections
              ? fullPlaylist.sections.map(s => ({ ...s, id: cuid() }))
              : [],
            tags: tags ? tags : [],
            default_zip_mode,
            share_mode,
            track_aliases,
            playlist_aliases: playlist_aliases || {},
            user_aliases,
            watermark,
            watermarking_status,
            playlist_type,
            release_date,
            tracks_album_image,
            tracks_artworks,
          }
          captureNoGroupsMessage(newShortlist, user, groups, playlist)
          dispatch(
            addInboxOnlyItemsToTeamLibrary(
              // @ts-expect-error - TODO: fix this
              [fullPlaylist],
              fullPlaylist.tracks.map(trackId => getState().tracks[trackId]),
            ),
          )
          dispatch(newShortlist)
          dispatch(activateShortlist(newShortlist.result.id))
        })
        .catch(error => {
          dispatch(sendGenericError(error))
        })
    } else {
      if (createOptions?.tracks) {
        newShortlist.result = {
          ...newShortlist.result,
          items: (createOptions?.tracks ?? []).map((track, id) => ({
            track,
            id,
          })),
        }

        if (createOptions?.tracks && createOptions?.tracks.length > 0) {
          fetchNestedTracks(createOptions?.tracks.map(t => t.id))
        }

        captureNoGroupsMessage(newShortlist, user, groups)
        dispatch(
          addInboxOnlyItemsToTeamLibrary([], createOptions?.tracks ?? []),
        )
        dispatch(newShortlist)
        dispatch(activateShortlist(newShortlist.result.id))
      } else if (createOptions?.playlist) {
        api
          .getFullPlaylist(createOptions?.playlist)
          .then(result => {
            const fullPlaylist = mapResponseToReducerObjects(
              dispatch,
              getState,
              result,
            )
            dispatch(fetchNestedTracks(fullPlaylist.tracks))
            newShortlist.result = {
              ...newShortlist.result,
              items: getShortlistItems(fullPlaylist, getState),
            }
            captureNoGroupsMessage(newShortlist, user, groups)
            dispatch(
              addInboxOnlyItemsToTeamLibrary(
                // @ts-expect-error - TODO: fix this
                [fullPlaylist],
                fullPlaylist.tracks.map(trackId => getState().tracks[trackId]),
              ),
            )
            dispatch(newShortlist)
            dispatch(activateShortlist(newShortlist.result.id))
          })
          .catch(error => {
            dispatch(sendGenericError(error))
          })
      } else {
        captureNoGroupsMessage(newShortlist, user, groups)
        await dispatch(newShortlist)
        if (!newShortlist.result.hidden)
          dispatch(activateShortlist(newShortlist.result.id))
      }
    }
  }
}

const getShortlistItems = (playlist, getState): ShortlistTrackItem[] =>
  playlist.tracks.map((trackId, index) => ({
    track: getState().tracks[trackId],
    id: index,
    uid: cuid(),
  }))

function captureNoGroupsMessage(
  newShortlist,
  user,
  groups,
  playlist?: Playlist,
) {
  if (newShortlist.result.groups.length === 0) {
    captureError(new Error('createNewShortlist: shortlist has no groups'), {
      extra: {
        level: 'warning',
        shortlist: newShortlist.result,
        user_groups: user.groups,
        loaded_groups: groups.groups.filter(g =>
          LIBRARY_TYPES.includes(g.group_type),
        ),
        playlist: playlist,
      },
    })
  }
}

/**
 * Updates fields of an existing shortlist.
 * @param shortlistId - ID of the shortlist to update
 * @param updatedFields - Object containing fields to update
 * @returns Action object to update the shortlist
 */
export function updateShortlist(
  shortlistId: string, // TODO: fix SavePlaylistData type
  updatedFields: Record<string, any>,
): {
  result: any
  shortlistId: string
  type: string
} {
  if (updatedFields.groups && updatedFields.groups.length === 0) {
    captureError(new Error('updateShortlist: shortlist has no groups'), {
      extra: {
        level: 'warning',
        updatedFields,
      },
    })
  }

  return {
    type: SHORTLIST_UPDATE,
    shortlistId,
    result: { ...updatedFields, saved: false },
  }
}
/**
 * Updates a temporary item in a shortlist during upload process.
 * @param shortlistId - ID of the shortlist containing the temp item
 * @param tempItem - Temporary item object with updated data
 * @param finalUpdate - Whether this is the final update signaling successful upload
 * @returns Thunk that updates the temp item and handles completion logic
 */
export function updateTempItem(
  shortlistId: string,
  tempItem: Record<string, any>, // final update signals that track successfully uploaded to server
  // and we no longer need to treat it as temporary item and can start
  // to treat it as regular track
  finalUpdate?: boolean,
): any {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    dispatch({
      type: SHORTLIST_TEMP_ITEM_UPDATE,
      shortlistId,
      tempItem,
      finalUpdate,
    })
    // Get the updated state after the item has been removed
    const shortlist = getState().shortlists.byId[shortlistId]

    // Check if tempItems is empty, nothing uploading
    if (shortlist && shortlist.tempItems?.length === 0) {
      // Check if there is a task to execute after upload completion
      if (shortlist.uploadCompletionTask) {
        if (shortlist.uploadCompletionTask.type === 'show_tracks') {
          const firstPane = getExplorerFirstPane(getState())

          if (
            firstPane?.groupType &&
            LIBRARY_TYPES.includes(firstPane.groupType)
          ) {
            dispatch(
              switchPaneView(firstPane, TRACK_VIEW, {
                sortParam: TRACK_DATE_SORT,
                sortDirection: SORT_DIRECTION_DESC,
              }),
            )
          }
        }
      }

      // Resolve the upload promise if it exists
      const promise = api.pendingUploadPromises[shortlistId]
      if (promise) promise.resolve(shortlist)
      // Remove the shortlist if it's hidden
      if (shortlist.hidden) dispatch(deleteShortlist(shortlistId))
    }
  }
}
/**
 * Updates upload progress for a temporary item in a shortlist.
 * @param shortlistId - ID of the shortlist containing the temp item
 * @param params - Progress update parameters
 * @param params.tempId - ID of the temporary item
 * @param params.progress - Upload progress percentage
 * @param params.status - Current upload status
 * @param params.externalUploadId - External upload identifier
 * @returns Action object to update temp item progress
 */
export function updateTempItemProgress(
  shortlistId: string,
  {
    tempId,
    progress,
    status,
    externalUploadId,
  }: {
    tempId: string
    progress: number
    status?: 'processing'
    externalUploadId: number
  },
): {
  shortlistId: string
  type: string
  update: {
    externalUploadId: number
    progress: number
    status: void | 'processing'
    tempId: string
  }
} {
  return {
    type: SHORTLIST_TEMP_ITEM_PROGRESS_UPDATE,
    shortlistId,
    update: {
      tempId,
      progress,
      status,
      externalUploadId,
    },
  }
}
/**
 * Processes completion of an external upload for a temporary item.
 * @param shortlistId - ID of the shortlist containing the upload
 * @param upload - Upload completion data
 * @param upload.tempId - ID of the temporary item
 * @param upload.track - Final track object from upload
 * @returns Thunk that finalizes the upload and updates the temp item
 */
export function processExternalUploadFinish(
  shortlistId: string,
  upload: {
    tempId: string
    track: Track
  },
): (dispatch: any, getState: GetState) => any | void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const state = getState()
    const shortlist = getShortlistById(state, {
      id: shortlistId,
    })
    if (!shortlist) return
    // Track can be deleted before upload done
    const tempItem = getShortlistTempItem(getState(), {
      id: shortlistId,
      tempItemId: upload.tempId,
    })
    if (!tempItem) return
    const trackId = upload.track.id
    const existingTrack = getTrack(state, {
      id: trackId,
    })
    // sometimes track_update notification arrives before external_upload_finished,
    // in that case external_upload_finished can rewrite track with less data causing track
    // to appear in "Processing" state and without artwork etc
    const uploadedTrack = existingTrack ? existingTrack : upload.track
    dispatch(loadBatch([uploadedTrack]))
    return dispatch(
      updateTempItem(
        shortlistId,
        {
          tempId: upload.tempId,
          ...uploadedTrack,
        },
        true,
      ),
    )
  }
}
/**
 * Inserts temporary items into a shortlist during upload process.
 * @param shortlistId - ID of the shortlist to insert items into
 * @param tempItems - Array of temporary items to insert
 * @param position - Optional position to insert items at
 * @param targetSection - Optional target section for insertion
 * @returns Action object to insert temporary items
 */
export function insertTempItems(
  shortlistId: string, // TODO: unify TempItem type
  tempItems: Record<string, any>[],
  position?: number,
  targetSection?: ShortlistSection,
): {
  position: void | number
  shortlistId: string
  targetSection: void | ShortlistSection
  tempItems: Array<any>
  type: string
} {
  return {
    type: SHORTLIST_TEMP_ITEM_INSERT,
    shortlistId,
    tempItems,
    position,
    targetSection,
  }
}
/**
 * Removes a temporary item from a shortlist.
 * @param shortlistId - ID of the shortlist to remove item from
 * @param tempItem - Temporary item to remove
 * @returns Action object to remove the temporary item
 */
export function removeTempItem(
  shortlistId: string, // TODO: unify TempItem type
  tempItem: Record<string, any>,
): {
  shortlistId: string
  tempItem: any
  type: string
} {
  return {
    type: SHORTLIST_TEMP_ITEM_REMOVE,
    shortlistId,
    tempItem,
  }
}
/**
 * Toggles the active state of a shortlist.
 * @param shortlistId - ID of the shortlist to toggle
 * @returns Thunk that toggles the shortlist's active state
 */
export function toggleShortlistActive(
  shortlistId: string,
): (dispatch: any, getState: GetState) => any {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { isActive } = getState().shortlists.byId[shortlistId]
    return dispatch({
      type: SHORTLIST_UPDATE,
      shortlistId,
      result: {
        isActive: !isActive,
      },
    })
  }
}
/**
 * Inserts a playlist into a shortlist by adding all its tracks.
 * @param shortlistId - ID of the shortlist to insert into
 * @param item - Object containing the playlist to insert
 * @param positionId - Optional position to insert at
 * @param targetSection - Optional target section for insertion
 * @returns Thunk that inserts the playlist tracks
 */
export function insertPlaylist(
  shortlistId: string,
  item: {
    playlist: Playlist
  },
  positionId?: number,
  targetSection?: ShortlistSection,
): (dispatch: any, getState: GetState) => Promise<void> {
  return insertSelectedItems(
    shortlistId,
    [item.playlist],
    positionId,
    targetSection,
  )
}

const addInboxOnlyItemsToTeamLibrary =
  (playlists: Playlist[], tracks: Track[]) => (dispatch, getState) => {
    const state = getState()
    const groups = getGroups(state)
    const teamLibrary = getTeamLibrary(groups)
    const playlistsToTeamLib = playlists.filter(item =>
      itemRequireAddingToTeamLib(item.groups, groups),
    )

    if (teamLibrary && playlistsToTeamLib.length) {
      dispatch(alterPlaylistGroups(playlistsToTeamLib, [teamLibrary], []))
    }

    const tracksToTeamLib = tracks.filter(item =>
      itemRequireAddingToTeamLib(item.groups, groups),
    )

    if (teamLibrary && tracksToTeamLib.length) {
      dispatch(alterTrackGroups(tracksToTeamLib, [teamLibrary], []))
    }
  }

function nestedTracksValidation({
  allDroppedTracks,
  shortlistId,
}: {
  allDroppedTracks: Track[]
  shortlistId: string
}) {
  return (_dispatch, getState) => {
    const nestingEnabled = isNestingEnabledInShortlist(getState(), shortlistId)
    if (!nestingEnabled) return true
    const shortlist = getShortlistById(getState(), {
      id: shortlistId,
    })
    const items = shortlist?.items || []
    const tracksInShortlist = items.map(({ track }) => track)
    const allMainTracksInShortlist = tracksInShortlist.filter(
      track => getNestedTracksCount(track) > 0,
    )

    const parentWarningTrack = allMainTracksInShortlist.find(mainTrack => {
      const nestedTrackIds = mainTrack.nested_tracks?.map(({ id }) => id) || []
      return allDroppedTracks.some(({ id }) => nestedTrackIds.includes(id))
    })

    if (parentWarningTrack) {
      const nestedWarningTrack = allDroppedTracks.find(({ id }) =>
        (parentWarningTrack.nested_tracks?.map(t => t.id) || []).includes(id),
      )

      if (nestedWarningTrack) {
        NavigationService.push(
          `/explorer/tracks/${nestedWarningTrack.id}/nesting-validation`,
          {
            state: {
              type: NESTED_TRACK_ALREADY_IN_PLAYLIST,
              track: nestedWarningTrack.id,
              duplicateTrack: parentWarningTrack.id,
            },
          },
        )
      }
    }

    return !parentWarningTrack
  }
}

/**
 * Normalizes nested tracks in all visible shortlists to prevent conflicts.
 * @returns Thunk that validates and removes conflicting nested tracks
 */
export function normalizeNestedTracksInShortlists(): (
  dispatch: any,
  getState: GetState,
) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const shortlistIds = getState().shortlists.visibleItems
    shortlistIds.forEach(shortlistId => {
      const nestingEnabled = isNestingEnabledInShortlist(
        getState(),
        shortlistId,
      )
      if (!nestingEnabled) return
      const shortlist = getShortlistById(getState(), {
        id: shortlistId,
      })
      const items = shortlist?.items || []
      const tracksInShortlist = items.map(({ track }) => track)
      const mainTrackIds = tracksInShortlist
        .map(track => (getNestedTracksCount(track) > 0 ? track.id : null))
        .filter(trackId => trackId !== null)
      const indexesToRemove: number[] = []
      tracksInShortlist.forEach((track, index) => {
        if (
          track?.nesting_parent_id &&
          mainTrackIds.includes(track.nesting_parent_id)
        ) {
          indexesToRemove.push(index)
        }
      })

      if (indexesToRemove.length !== 0) {
        const nestedTrack = tracksInShortlist[Number(indexesToRemove[0])]
        const mainTrack = tracksInShortlist.find(
          track => track.id === nestedTrack?.nesting_parent_id,
        )

        if (mainTrack) {
          NavigationService.push(
            `/explorer/tracks/${mainTrack.id}/nesting-validation`,
            {
              state: {
                type: VERSION_OF_MAIN_TRACK_ALREADY_IN_PLAYLIST,
                track: nestedTrack?.id,
                duplicateTrack: nestedTrack?.nesting_parent_id,
              },
            },
          )
        }

        dispatch(removeSelectedItems(shortlistId, indexesToRemove))
      }
    })
  }
}

function tempTrackItem(track: Track): Record<string, any> {
  return {
    tempId: cuid(),
    filename: track.name,
    title: track.name,
    album: undefined,
    artist: undefined,
    ext: track.ext,
    progress: 0,
    importing: true,
    file: null,
    type: TRACK,
    groups: [],
  }
}

/**
 * Inserts an aggregated track from external sources into a shortlist.
 * @param shortlistId - ID of the shortlist to insert into
 * @param track - Aggregated track to import and insert
 * @param positionId - Optional position to insert at
 * @param targetSection - Optional target section for insertion
 * @returns Thunk that imports and inserts the aggregated track
 */
export function insertAggregatedTrack(
  shortlistId: string,
  track: Track,
  positionId?: number,
  targetSection?: ShortlistSection,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    const tempItem = tempTrackItem(track)
    dispatch(
      insertTempItems(shortlistId, [tempItem], positionId, targetSection),
    )
    const importedTrack = await dispatch(
      addAggregatedTrackToDisco({
        track,
        includeNestedTracks: true,
        includeStems: true,
      }),
    )

    if (importedTrack) {
      dispatch(
        updateTempItem(
          shortlistId,
          {
            tempId: tempItem.tempId,
            ...importedTrack,
          },
          true,
        ),
      )
    } else {
      dispatch(removeTempItem(shortlistId, tempItem))
    }
  }
}

/**
 * Inserts tracks from an external playlist into a shortlist.
 * @param shortlistId - ID of the shortlist to insert into
 * @param playlist - External playlist containing tracks to import
 * @param positionId - Optional position to insert at
 * @param targetSection - Optional target section for insertion
 * @param sourceGroupId - Optional source group identifier
 * @returns Thunk that imports and inserts external playlist tracks
 */
export function insertExternalPlaylistTracks(
  shortlistId: string,
  playlist: Playlist,
  positionId?: number,
  targetSection?: ShortlistSection,
  sourceGroupId?: number,
): (dispatch: any) => Promise<void> {
  return async (dispatch: (...args: any[]) => any) => {
    try {
      const fullPlaylist = await api.getFullPlaylist(playlist)
      const tempItems = fullPlaylist.tracks.map(track => tempTrackItem(track))

      for (const tempItem of tempItems) {
        dispatch(
          insertTempItems(shortlistId, [tempItem], positionId, targetSection),
        )
      }
      const importedPlaylistTracks = await dispatch(
        copyPlaylistTracksToDisco(fullPlaylist, sourceGroupId),
      )
      if (importedPlaylistTracks) {
        for (const tempItem of tempItems) {
          const importedTrack = importedPlaylistTracks.find(
            track => track.name === tempItem.title,
          )
          if (importedTrack) {
            dispatch(
              updateTempItem(
                shortlistId,
                {
                  tempId: tempItem.tempId,
                  ...importedTrack,
                },
                true,
              ),
            )
          } else {
            dispatch(removeTempItem(shortlistId, tempItem))
          }
        }
      }
    } catch (error) {
      console.error('Error adding playlist to Disco:', error)
    }
  }
}

export const getExternalSelectionFilteredTracks = async (
  selectedItems: (Playlist | Track)[],
): Promise<Track[]> => {
  const filteredTracks = await Promise.all(
    selectedItems.map(async item => {
      switch (item.type) {
        case PLAYLIST: {
          const fullPlaylist = await api.getFullPlaylist(item)
          const playlistTracks = fullPlaylist.tracks.filter(
            track => track.type === TRACK,
          )
          return playlistTracks
        }
        case TRACK:
          return [item]
        default:
          return []
      }
    }),
  )

  return uniqBy(filteredTracks.flat(), 'id')
}

export function insertExternalTrackBatch(
  shortlistId: string,
  selectedItems: (Playlist | Track)[],
  sourceGroupId: GroupId,
  positionId?: number,
  targetSection?: ShortlistSection,
): (dispatch: any) => Promise<void> {
  return async (dispatch: (...args: any[]) => any) => {
    const selectedTracks = await getExternalSelectionFilteredTracks(
      selectedItems,
    )

    if (!selectedTracks.length) return
    // create temp items
    const tempItems = selectedTracks.map(track => tempTrackItem(track))
    dispatch(insertTempItems(shortlistId, tempItems, positionId, targetSection))
    const importedTracks = await dispatch(
      copyBulkTracksToDisco(selectedTracks, sourceGroupId),
    )

    if (importedTracks?.length) {
      const tempItemsByTitle = new Map(
        tempItems.map(item => [item.title, item]),
      )
      importedTracks
        .map(importedTrack => ({
          importedTrack,
          tempItem: tempItemsByTitle.get(importedTrack.name),
        }))
        .filter(({ tempItem }) => Boolean(tempItem))
        .map(({ importedTrack, tempItem }) => {
          dispatch(
            updateTempItem(
              shortlistId,
              {
                tempId: tempItem.tempId,
                ...importedTrack,
              },
              true,
            ),
          )
        })
    } else {
      tempItems.map(tempItem => dispatch(removeTempItem(shortlistId, tempItem)))
    }
  }
}

/**
 * Inserts selected items (tracks and playlists) into a shortlist.
 * @param shortlistId - ID of the shortlist to insert into
 * @param selectedItems - Array of selected tracks and playlists
 * @param positionId - Optional position to insert at
 * @param targetSection - Optional target section for insertion
 * @returns Thunk that processes and inserts the selected items
 */

const getTempShortlistTracks = (
  selectedItems: Record<string, any>[],
  tracks?: Track[],
  fullPlaylists?: Playlist[],
): Track[] => {
  if (!selectedItems.length || (!tracks && !fullPlaylists)) return []
  const resultTracks: Track[] = []
  selectedItems.forEach(item => {
    if (item.type === TRACK && tracks?.[item.id]) {
      const track = tracks[item.id]
      if (track) {
        resultTracks.push(track)
      }
    }
    if (item.type === TRACK && item.tempId) {
      resultTracks.push(item as Track)
    }

    if (item.type === PLAYLIST) {
      const playlist = fullPlaylists?.find(fullPlaylist => {
        return fullPlaylist.playlist_id === item.playlist_id
      })

      if (playlist) {
        resultTracks.push(...playlist.tracks)
      }
    }
  })
  return uniqBy(resultTracks, 'id')
}

export function insertSelectedItems(
  shortlistId: string,
  selectedItems: Record<string, any>[],
  positionId?: number,
  targetSection?: ShortlistSection,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: any[]) => any, getState: GetState) => {
    const state = getState()
    const statePlaylists = getPlaylists(state)

    const tracks = getTracks(state)
    const playlistIds = selectedItems
      .filter(item => item.type === PLAYLIST)
      .map((item: Playlist) => item.playlist_id)
    const playlists = playlistIds.map(id => statePlaylists[id])
    const fullPlaylists = await Promise.all(
      playlists.map(playlist => api.getFullPlaylist(playlist)),
    )
    const reducerPlaylists = mapResponseToReducerObjects(
      dispatch,
      getState,
      fullPlaylists,
    )
    dispatch(
      fetchNestedTracks([
        ...reducerPlaylists.flatMap(p => p.tracks),
        ...selectedItems.filter(t => t.type === TRACK).map(t => t.id),
      ]),
    )
    const transformedTracks = getTempShortlistTracks(
      selectedItems,
      tracks,
      fullPlaylists,
    )

    dispatch(
      addInboxOnlyItemsToTeamLibrary(
        fullPlaylists,
        transformedTracks.filter(t => t.id),
      ),
    )
    const valid = dispatch(
      nestedTracksValidation({
        allDroppedTracks: transformedTracks,
        shortlistId,
      }),
    )
    if (!valid) return
    dispatch({
      type: SHORTLIST_PLAYLIST_TRACKS_ADD,
      result: {
        shortlistId,
        tracks: transformedTracks,
        position: positionId,
        targetSection,
      },
    })
    dispatch(normalizeNestedTracksInShortlists())
  }
}
/**
 * Moves selected items to a new position within a shortlist.
 * @param shortlistId - ID of the shortlist containing items to move
 * @param selectedItemIndexes - Array of item indexes to move
 * @param positionId - Target position to move items to
 * @param targetSection - Target section for the moved items
 * @returns Thunk that moves the selected items and updates sections
 */
export function moveSelectedItems(
  shortlistId: string,
  selectedItemIndexes: number[],
  positionId: number,
  targetSection: ShortlistSection,
): (dispatch: any, getState: GetState) => any {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const shortlist = getState().shortlists.byId[shortlistId]
    const { items } = shortlist
    const newItems: ShortlistTrackItem[] = moveItems2(
      items,
      selectedItemIndexes,
      positionId,
    )
    const newItemsWithCorrectIds = removeGapsInIds(newItems)
    const sections = adjustSectionsOnMovedTrackIndexes(
      shortlist,
      selectedItemIndexes,
      positionId,
      targetSection,
    )
    return dispatch({
      type: SHORTLIST_UPDATE,
      shortlistId,
      result: {
        items: newItemsWithCorrectIds,
        sections,
      },
    })
  }
}
// created to move tracks by shortlist in mobile web
/**
 * Shifts a shortlist item up or down by one position.
 * @param shortlistId - ID of the shortlist containing the item
 * @param position - Current position of the item to shift
 * @param direction - Direction to shift (1 for down, -1 for up)
 * @returns Thunk that shifts the item position
 */
export function shiftShortlistItem(
  shortlistId: string,
  position: number,
  direction: number, // 1 | -1
): (dispatch: any, getState: GetState) => any | void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    try {
      const shortlist = getState().shortlists.byId[shortlistId]
      const newPosition = position + direction
      const { items } = shortlist
      const newItems = cloneDeep(items)
      let updateItemsPos = true
      let sections = shortlist.sections as ShortlistSection[]
      let sectionToUpdate: ShortlistSection | null = null
      let sectionPosition = newPosition

      if (sections) {
        // trying to find a section when moving a track down or up
        if (direction < 0) {
          const reversedSections = [...sections].reverse()
          sectionToUpdate =
            reversedSections.find(s => s.position === position) ?? null
          sectionPosition = position + direction * -1
        } else {
          sectionToUpdate =
            sections.find(s => s.position === newPosition) ?? null
          sectionPosition = position
        }

        // if there is a section on bottom or top of the track,
        // then only section needs to be moved
        if (sectionToUpdate) {
          updateItemsPos = false
          sections = sections.map(s => {
            if (s.id === sectionToUpdate?.id) {
              return { ...s, position: sectionPosition }
            }

            return s
          })
        }
      }

      // if there are no sections after track/before track,
      // then we need to move the track
      if (updateItemsPos) {
        newItems[newPosition] = {
          id: newPosition,
          track: items[position].track,
        }
        newItems[position] = items[newPosition]
      }

      const newItemsWithCorrectIds = removeGapsInIds(newItems)
      return dispatch({
        type: SHORTLIST_UPDATE,
        shortlistId,
        result: {
          items: newItemsWithCorrectIds,
          sections,
        },
      })
    } catch (error) {
      captureError(error)
    }
  }
}
/**
 * Sorts tracks in a shortlist by the specified key and direction.
 * @param shortlistId - ID of the shortlist to sort
 * @param key - Sort key (e.g., title, artist, date)
 * @param direction - Sort direction (ascending or descending)
 * @returns Thunk that sorts the shortlist tracks
 */
export function sortShortlistTracks(
  shortlistId: string,
  key: SortKey,
  direction: SortDirection,
): (dispatch: any, getState: GetState) => any {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const tracks = getState().tracks
    const shortlist = getState().shortlists.byId[shortlistId]
    // use actual tracks
    const items = shortlist.items.map(item => ({
      ...item,
      track: tracks[item.track.id],
    }))
    const sortedItems =
      shortlist.sections && shortlist.sections.length
        ? sortItemsInSections(shortlist.sections, items, key, direction)
        : sortItems(items, key, direction)
    const resultItems = sortedItems.map((item, index) => ({
      ...item,
      id: index,
    }))
    return dispatch({
      type: SHORTLIST_UPDATE,
      shortlistId,
      result: {
        sort_key: key,
        sort_direction: direction,
        items: resultItems,
      },
      meta: {
        mixpanel: {
          event: 'Change track order shortlist',
        },
      },
    })
  }
}
/**
 * Removes selected items from a shortlist.
 * @param shortlistId - ID of the shortlist to remove items from
 * @param selectedItemIndexes - Array of item indexes to remove
 * @returns Thunk that removes items and handles playback state
 */
export function removeSelectedItems(
  shortlistId: string,
  selectedItemIndexes: (number | string)[],
): (dispatch: any, getState: GetState) => any {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const shortlist = getState().shortlists.byId[shortlistId]
    const { items, playlist_id } = shortlist
    const indexes: number[] = selectedItemIndexes.map(Number)
    // we had to notify player that it needs to jump to next track if
    // removing items contain currently playing track
    const { trackShortlistId, trackOriginalId } = getState().media

    if (trackShortlistId === shortlistId) {
      const tracksToRemove = indexes.map(index => {
        return {
          id: items[index].track.id,
          index,
        }
      })
      const playingTrack = tracksToRemove.find(
        track => track.id === trackOriginalId,
      )

      if (playingTrack) {
        let nextTrackIndex = -1

        for (let i = playingTrack.index; i < items.length; i++) {
          if (indexes.indexOf(i) === -1) {
            nextTrackIndex = i
            break
          }
        }

        if (nextTrackIndex > -1) {
          dispatch(
            playTrack(items[nextTrackIndex].track.id, 'in-app', {
              playlistId: playlist_id,
              shortlistId,
              paneIndex: null,
            }),
          )
        } else {
          dispatch(resetPlaybackState())
        }
      }
    }

    const newItems = items.filter((_item, index) => {
      return indexes.indexOf(index) === -1
    })
    const correctedNewItems = removeGapsInIds(newItems)
    return dispatch(
      updateShortlist(shortlistId, {
        items: correctedNewItems,
        sections: adjustSectionsOnRemovedTrackIndexes(shortlist, indexes),
      }),
    )
  }
}
/**
 * Removes gaps in item IDs by reassigning sequential IDs.
 * @param items - Array of shortlist track items to normalize
 * @returns Array of items with sequential IDs starting from 0
 */
export function removeGapsInIds(
  items: ShortlistTrackItem[],
): ShortlistTrackItem[] {
  return items.map((currentItem, index) => {
    return { ...currentItem, id: index }
  })
}
/**
 * Inserts a single track item into a shortlist.
 * @param shortlistId - ID of the shortlist to insert into
 * @param item - Object containing the track to insert
 * @param positionId - Optional position to insert at
 * @param targetSection - Optional target section for insertion
 * @returns Thunk that inserts the track item
 */
export function insertItem(
  shortlistId: string,
  item: {
    track: Track
  },
  positionId?: number,
  targetSection?: ShortlistSection,
): (dispatch: any, getState: GetState) => Promise<void> {
  return insertSelectedItems(
    shortlistId,
    [item.track],
    positionId,
    targetSection,
  )
}
/**
 * Saves showcase data including artist information and images.
 * @param params - Showcase save parameters
 * @param params.playlistData - Playlist data to save
 * @param params.shortlist - Current shortlist state
 * @param params.album_image - Optional album image file or base64 string
 * @returns Thunk that saves showcase data and handles image updates
 */
export function saveShowcaseData({
  playlistData,
  shortlist,
  album_image,
}: {
  playlistData: Shortlist
  // TODO: fix SavePlaylistData type
  shortlist: Shortlist
  album_image?: string | (File | null | undefined)
}): (dispatch: any, getState: GetState) => Promise<unknown> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const clonePlaylistData = cloneDeep<Shortlist>(playlistData)
    const showcase = playlistData.showcase

    if (
      showcase?.updateArtistBio ||
      !isEmpty(showcase?.artist?.updatedFields)
    ) {
      const updatedFields = {
        bio_editor: showcase?.artist?.updatedFields?.bio_editor,
        bio_html: showcase?.artist?.updatedFields?.bio_html,
        ...showcase?.artist?.updatedFields,
      }

      if (showcase?.updateArtistBio) {
        updatedFields.bio_editor =
          showcase?.artist?.updatedFields?.bio_editor ||
          showcase?.artist_override_bio_editor
        updatedFields.bio_html =
          showcase?.artist?.updatedFields?.bio_html ||
          showcase?.artist_override_bio_html
      }

      if (showcase?.artist?.id) {
        await dispatch(updateArtist(showcase.artist.id, updatedFields))

        if (showcase?.artist?.updatedFields) {
          delete showcase?.artist.updatedFields
        }
      }
    }

    let artist_override_image: string | null = null

    if (showcase?.artist_override_image) {
      artist_override_image = showcase.artist_override_image
      showcase.artist_override_image = null
      delete showcase.artist_override_image_preview
    }

    // savePlaylist rejects (and dispatches a generic error) on failure; let it
    // propagate to the caller (saveShortlist) which resets the saving state,
    // surfaces the server error, and rejects to the save modal.
    const serverPlaylist = cloneDeep(
      await dispatch(savePlaylist(clonePlaylistData)),
    )
    const isUpdateAlbum =
      (album_image ||
        (serverPlaylist.album_image_url && !shortlist.album_image_url) ||
        (Boolean(shortlist.color) &&
          shortlist.color !== serverPlaylist.color)) &&
      serverPlaylist.showcase

    if (isUpdateAlbum) {
      let updateAlbum = album_image || shortlist.album_image_url

      if (album_image && typeof album_image === 'string') {
        updateAlbum = await base64ToBlob(album_image)
      }

      const { album_image_url } = await api.updatePlaylist(
        serverPlaylist.playlist_id,
        {
          album_image: updateAlbum,
          color: shortlist.color || null,
        },
      )
      serverPlaylist.album_image_url = album_image_url
    }

    const isUpdateArtist =
      (artist_override_image ||
        (serverPlaylist.showcase?.artist_override_image_url &&
          !showcase?.artist_override_image_url)) &&
      serverPlaylist.showcase

    if (isUpdateArtist && showcase) {
      let newArtistImage: File | string | null =
        artist_override_image || showcase?.artist_override_image_url || null

      if (artist_override_image) {
        newArtistImage = await base64ToBlob(artist_override_image)
      }

      showcase.artist_override_image = null

      if (showcase.updateArtistImage && typeof newArtistImage !== 'string') {
        const response = await dispatch(
          updateArtistImage(serverPlaylist.showcase.artist.id, newArtistImage),
        )
        serverPlaylist.showcase.artist.image_url = response.image_url
        await dispatch(
          updateArtist(serverPlaylist.showcase.artist.id, {
            focal_points: showcase.focal_points,
          }),
        )
      } else {
        const overrideImage = await api.showcaseOverrideImage(
          serverPlaylist.showcase.id,
          [
            {
              field: 'artist_override_image',
              file: newArtistImage,
              filename:
                typeof newArtistImage === 'string'
                  ? undefined
                  : newArtistImage?.name,
            },
          ],
        )
        serverPlaylist.showcase.artist_override_image_url =
          overrideImage.artist_override_image
      }
    }

    if (isUpdateArtist || isUpdateAlbum) {
      mapResponseToReducerObjects(dispatch, getState, serverPlaylist)
    }

    return serverPlaylist
  }
}
/**
 * Saves default playlist options to user settings and applies to empty shortlists.
 * @param playlist - Shortlist containing the default options to save
 * @returns Thunk that saves default options and updates empty shortlists
 */
export function saveDefaultPlaylistOptions(
  playlist: Shortlist,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    try {
      const settings = {
        defaultPlaylistOptions: {
          show_lyrics: Boolean(playlist.show_lyrics),
          show_business_logo: Boolean(playlist.show_business_logo),
        },
      }
      const result = {
        settings,
      }
      dispatch({
        type: USER_SETTINGS_UPDATE_SUCCESS,
        result: result,
      })
      const emptyShortlists = getEmptyShortlists(getState())
      emptyShortlists.forEach(emptyShortlist => {
        dispatch(
          updateShortlist(emptyShortlist.id, settings.defaultPlaylistOptions),
        )
      })
      await api.updateUserSettings(result)
    } catch (error) {
      dispatch(sendGenericError(error))
    }
  }
}
/**
 * Saves a shortlist to the server with all its data and configurations.
 * @param shortlistId - ID of the shortlist to save
 * @param options - Optional save configuration
 * @param options.defaultTerritory - Default territory group for the playlist
 * @param options.imageFile - Optional image file for artwork alias
 * @returns Thunk that saves the shortlist and handles all related updates
 */
export function saveShortlist(
  shortlistId: string,
  options = {} as {
    defaultTerritory?: Group | null | undefined
    imageFile?: File | null | undefined
  },
): (dispatch: any, getState: GetState) => Promise<any> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const shortlist = getState().shortlists.byId[shortlistId]
    if (!shortlist) return
    const {
      album_image,
      focal_points,
      items,
      title,
      groups,
      playlist_id,
      is_client_version,
      download_links,
      show_lyrics,
      show_business_logo,
      show_contacts,
      showcase,
      include_nested_track_versions,
      share_stems,
      theme,
      sort_key,
      sort_direction,
      sections,
      tags,
      general_message,
      general_message_html,
      default_zip_mode,
      share_mode,
      track_aliases,
      playlist_aliases,
      watermark,
      watermarking_status,
      playlist_type,
      release_date,
    } = shortlist
    const track_ids = items.map(item => item.track.id)
    let filteredGroups = groups.filter(Boolean)
    const { defaultTerritory } = options

    if (defaultTerritory) {
      filteredGroups = filteredGroups.filter(g => g.group_type !== TERRITORY)

      if (filteredGroups.some(g => g.group_type === TEAM_LIBRARY)) {
        // playlist added to territory only when 'Viewable by the team'
        filteredGroups = filteredGroups.concat(defaultTerritory)
      }
    }

    const group_ids = filteredGroups.map(group => group.id)
    const showcasesEnabled = isFeatureEnabled(getState(), {
      featureName: SHOWCASES,
    })
    const nestingEnabled = isFeatureEnabled(getState(), {
      featureName: TRACK_NESTING,
    })
    const playlistData = {
      focal_points,
      name: title || '',
      is_client_version,
      download_links,
      show_lyrics,
      show_business_logo: showcasesEnabled ? show_business_logo : true,
      show_contacts,
      showcase: showcase?.type ? cloneDeep(showcase) : undefined,
      include_nested_track_versions: nestingEnabled
        ? include_nested_track_versions
        : null,
      share_stems: nestingEnabled ? share_stems : null,
      track_ids,
      group_ids,
      sort_key,
      sort_direction,
      general_message,
      general_message_html,
      theme: theme ? theme.url : null,
      sections: sections.map(s => ({
        name: s.name,
        position: s.position,
      })),
      tags,
      default_zip_mode,
      share_mode,
      track_aliases,
      playlist_aliases: playlist_aliases || {},
      playlist_id: playlist_id || undefined,
      watermark,
      watermarking_status,
      playlist_type,
      release_date,
    }
    dispatch({
      type: SHORTLIST_UPDATE,
      shortlistId,
      result: {
        saving: true,
      },
    })

    try {
      const serverPlaylist = await dispatch(
        saveShowcaseData({
          // @ts-expect-error - TODO: fix this
          playlistData,
          shortlist,
          album_image,
        }),
      )
      const artwork = playlistData.playlist_aliases?.Artwork

      if (artwork && artwork.value) {
        const { include, value } = artwork

        if (include && value === 'IMAGE_FILE' && options.imageFile) {
          await api.saveArtworkAlias(serverPlaylist.id, options.imageFile)
        }
      }

      await dispatch(saveDefaultPlaylistOptions(serverPlaylist))

      if (defaultTerritory) {
        try {
          const updated = await api.getPlaylist(serverPlaylist, {
            pageSize: 1,
            territories: [defaultTerritory.id],
          })
          const [view_url] = await api.shorturl([updated.view_playlist_url])
          // patch `view_playlist_url` for `ShortlistSaved` modal
          serverPlaylist.view_playlist_url = view_url
        } catch (error) {
          console.error('could not fetch view_playlist_url', error)
        }
      }

      dispatch({
        type: SHORTLIST_UPDATE,
        shortlistId,
        result: {
          saved: true,
          saving: false,
          playlist: serverPlaylist,
          playlist_id: serverPlaylist.playlist_id,
        },
      })
      dispatch(adjustPlaylistPositionInExplorerPane(serverPlaylist))

      if (is_client_version) {
        dispatch(sendMixpanelEvent('Save client list'))
      }

      if (!download_links) {
        dispatch(sendMixpanelEvent('Save streaming only playlist'))
      }

      if (
        groups &&
        groups.length === 1 &&
        groups[0].group_type === PERSONAL_LIBRARY
      ) {
        dispatch(sendMixpanelEvent('Save personal only playlist'))
      }

      if (
        groups &&
        groups.length > 0 &&
        groups.some(g => g.group_type === CUSTOM_CHANNEL)
      ) {
        dispatch(sendMixpanelEvent('Save to channels in save modal'))
      }

      if (general_message) {
        dispatch(sendMixpanelEvent('Add playlist message'))
      }

      // Update recents section playlist data
      dispatch(updateRecentsWithNewItem(serverPlaylist, 'playlist'))

      return serverPlaylist
    } catch (error) {
      dispatch({
        type: SHORTLIST_UPDATE,
        shortlistId,
        result: {
          saving: false,
        },
        error,
      })
      pushUINotification({
        message: parseServerError(error.body, {
          fallback:
            'Sorry! A server error prevented this playlist from saving. Please try again, and if it fails, let us know.',
        }),
        level: 'error',
      })
      throw error
    }
  }
}
/**
 * Saves playlist data to the server and maps response to reducer objects.
 * @param playlistData - Shortlist data to save as a playlist
 * @returns Thunk that saves the playlist and handles the response
 */
export function savePlaylist(
  playlistData: Shortlist,
): (dispatch: any, getState: GetState) => Promise<unknown> {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    return api
      .savePlaylist(removeBinaryImagesInShortlist<Shortlist>(playlistData))
      .then(playlist => {
        return mapResponseToReducerObjects(dispatch, getState, playlist)
      })
      .catch(error => {
        dispatch(sendGenericError(error))
        throw error
      })
  }
}
/**
 * Adds a new section to a shortlist.
 * @param shortlistId - ID of the shortlist to add section to
 * @param name - Name of the new section
 * @param position - Position where to add the section (default: 0)
 * @param place - Optional placement context
 * @returns Action object to add the section
 */
export function addShortlistSection(
  shortlistId: string,
  name: string,
  position = 0,
  place?: string,
): {
  name: string
  position: number
  shortlistId: string
  type: string
  place: void | string
} {
  return {
    type: SHORTLIST_SECTION_ADD,
    shortlistId,
    name,
    position,
    place,
  }
}
/**
 * Updates an existing section in a shortlist.
 * @param shortlistId - ID of the shortlist containing the section
 * @param sectionId - ID of the section to update
 * @param name - New name for the section
 * @param position - Optional new position for the section
 * @param index - Optional new index for the section
 * @returns Action object to update the section
 */
export function updateShortlistSection(
  shortlistId: string,
  sectionId: string,
  name: string,
  position?: number,
  index?: number,
): {
  name: string
  position: void | number
  sectionId: string
  shortlistId: string
  type: string
  index: void | number
} {
  return {
    type: SHORTLIST_SECTION_UPDATE,
    shortlistId,
    sectionId,
    name,
    position,
    index,
  }
}
/**
 * Removes a section from a shortlist.
 * @param shortlistId - ID of the shortlist containing the section
 * @param sectionId - ID of the section to remove
 * @returns Action object to remove the section
 */
export function removeShortlistSection(
  shortlistId: string,
  sectionId: string,
): {
  sectionId: string
  shortlistId: string
  type: string
} {
  return {
    type: SHORTLIST_SECTION_REMOVE,
    shortlistId,
    sectionId,
  }
}
/**
 * Resets all shortlists to their initial state.
 * @returns Action object to reset shortlists
 */
export const resetShortlists = (): {
  type: string
} => ({
  type: SHORTLIST_RESET,
})

function getDeletedIds(source) {
  const result: number[] = []
  Object.keys(source).forEach(prop => {
    const trackId = parseInt(prop, 10)
    if (source[trackId] === null) result.push(trackId)
  })
  return result
}

const sanitizeState = (
  savedState: Record<string, any>[],
  serverState: Record<string, any>,
): Record<string, any>[] => {
  // sync tracks
  const deletedTracks = getDeletedIds(serverState.tracks)
  // sync playlists
  const deletedPlaylists = getDeletedIds(serverState.playlists)
  const sanitizedState = savedState.map(shortlist => {
    // saving state should be omitted
    const { saving, ...s } = shortlist
    const newShortlist: Partial<Shortlist> = {
      ...s,
      // if localStorage contains shortlists without id, sections or tags
      // (they were added later) ensure they will get those props
      id: s.id || cuid(),
      sections: s.sections || [],
      tags: s.tags || [],
      // omit deleted tracks from alias
      track_aliases: omit(s.track_aliases, deletedTracks.map(String)),
    }

    // if playlist has been deleted on server, but was saved in
    // localstorage on client, just remove playlist id and
    // work with it like a new one
    if (
      shortlist.playlist_id &&
      deletedPlaylists.includes(shortlist.playlist_id)
    ) {
      delete newShortlist.playlist_id
      newShortlist.saved = false
    }

    if (!newShortlist.items || !newShortlist.sections) {
      return newShortlist
    }

    // if server no longer has track saved locally, remove it
    for (let i = newShortlist.items.length - 1; i >= 0; i--) {
      const newShortlistItemsTrack = newShortlist?.items?.[i]?.track

      if (!newShortlistItemsTrack) {
        continue
      }

      const mergeTarget =
        newShortlist.items[i] &&
        serverState.track_merges &&
        serverState.track_merges[newShortlistItemsTrack.id]
      const trackRemoved =
        !newShortlist.items[i] ||
        deletedTracks.includes(newShortlistItemsTrack.id)

      if (
        mergeTarget &&
        !newShortlist.items.find(
          item => item && item.track.id === mergeTarget,
        ) &&
        serverState.tracks[mergeTarget] // target merged track can be deleted too
      ) {
        newShortlistItemsTrack.id = mergeTarget
      } else if (trackRemoved) {
        // remove deleted track
        newShortlist.items.splice(i, 1)
        // adjust section positions after removed track
        newShortlist.sections = newShortlist.sections.map(s => {
          const position = s.position > i ? s.position - 1 : s.position
          return { ...s, position }
        })
      }
    }

    newShortlist.items = newShortlist.items.map((item, id) => ({
      // set item id to index in case item ids have gaps after track removal
      id,
      track: serverState.tracks[item.track.id],
    }))
    return newShortlist
  })
  return sanitizedState
}

/**
 * Initializes shortlists from saved state with server synchronization.
 * @param savedState - Array of saved shortlists from local storage
 * @returns Thunk that loads and sanitizes shortlist state
 */
export const initShortlists =
  (savedState: Shortlist[]): ((dispatch: any) => Promise<any>) =>
  async (dispatch: (...args: Array<any>) => any) => {
    try {
      const savedPlaylists = savedState
        .filter(item => item.playlist_id)
        .map(item => item.playlist_id)
        .filter(id => id !== null && id !== undefined)
      const savedTracks: number[] = []
      savedState.forEach(savedItem => {
        if (savedItem) {
          savedTracks.push(
            ...savedItem.items
              .filter(item => item?.track?.id)
              .map(item => item.track.id),
          )
        }
      })

      if (savedPlaylists.length === 0 && savedTracks.length === 0) {
        dispatch({
          type: LOAD_SAVED_STATE,
          savedState,
        })
        return dispatch({
          type: START_SAVING_STATE,
        })
      }

      dispatch({
        type: SHORTLIST_SET_LOADING,
        loading: true,
      })
      const serverState = await api.getExistingObjects(
        savedPlaylists,
        null,
        savedTracks,
      )
      dispatch({
        type: SHORTLIST_SET_LOADING,
        loading: false,
      })
      const sanitizedSavedState = sanitizeState(savedState, serverState)
      const existingTracks = Object.keys(serverState.tracks).map(key => {
        return serverState.tracks[Number(key)]
      })
      dispatch(loadBatch(existingTracks))
      // fetch nested tracks if tracks returned from server contain them,
      // separate call is required as we need to rely on fresh data
      // from the previous API request
      dispatch(
        fetchNestedTracks(
          existingTracks
            .filter(track => track !== undefined && track !== null)
            .map(t => t.id),
        ),
      )
      dispatch({
        type: LOAD_SAVED_STATE,
        savedState: sanitizedSavedState,
      })
      dispatch({
        type: START_SAVING_STATE,
      })
    } catch (error) {
      return handleShortlistLoadingError(error, savedState, dispatch)
    }
  }

const handleShortlistLoadingError = (
  error: Error,
  savedState,
  dispatch,
): Promise<{
  type: string
  error: Error
}> => {
  captureError(error, { extra: savedState })
  return dispatch({
    type: LOAD_SAVED_STATE_ERROR,
    error,
  })
}

/**
 * Starts the process of saving shortlist state to local storage.
 * @returns Action object to start saving state
 */
export const startSavingShortlistState = (): {
  type: string
} => ({
  type: START_SAVING_STATE,
})
/**
 * Initializes edited track state for a shortlist.
 * @param shortlistId - ID of the shortlist to initialize edited track for
 * @returns Thunk that initializes edited track with common metadata and groups
 */
export const initShortlistEditedTrack: (
  shortlistId: string,
) => (
  dispatch: (...args: Array<any>) => any,
  getState: GetState,
) => void = shortlistId => {
  return (dispatch, getState) => {
    const shortlist = getShortlistById(getState(), {
      id: shortlistId,
    })

    if (shortlist) {
      dispatch(
        initEditedTrack({
          id: null,
          url: null,
          metadata: getInitialMetadataValues(shortlist),
          comments: [],
          track_writers: [],
          groups: getCommonGroups(shortlist.items.map(item => item.track)),
        }),
      )
    }
  }
}

/**
 * Creates a showcase shortlist for an artist with all their tracks.
 * @param artistId - ID of the artist to create showcase for
 * @returns Thunk that loads artist tracks and creates showcase shortlist
 */
export function createShowcase(artistId: ArtistId): Thunk {
  return async (dispatch, getState) => {
    await dispatch(loadAllArtistTracks(artistId))
    const state = getState()
    const artist = getArtist(state, artistId)
    const tracks = getArtistTracks(state, artistId)
    const title = `${artist.name} - Artist Page`
    const options = {
      tracks: tracks?.list ?? [],
      defaultFields: {
        showcase: {
          artist,
          color_theme: 'dark',
          type: 'artist',
        },
      },
    }
    dispatch(createNewShortlist(undefined, title, options))
  }
}
